import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  Check,
  X,
  Clock,
  AlertCircle,
  Loader2,
  LogIn,
  Home,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, useAuthActions } from "@/stores/auth/auth-store";
import {
  useInvitationStore,
  type AcceptInvitationResult,
} from "@/stores/invitation/invitation-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { api } from "@/services/api/client";
import {
  FOREST_GREEN,
  ERROR,
  WARNING,
  TEXT,
  BACKGROUND,
  MID_GRAY,
  SHADOWS,
} from "@/styles/tokens";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";
import * as errorUtils from '@/utils/errors';
import { getInvitationAcceptRedirect } from "@/utils/invitationRedirect";

/**
 * Invitation details returned from the validation endpoint
 */
interface InvitationDetails {
  status?:
    | "pending"
    | "already_accepted"
    | "already_declined"
    | "expired"
    | "canceled"
    | "invalidated"
    | "removed";
  membershipId?: string;
  organization: {
    _id?: string;
    name: string;
    slug: string;
    description?: string;
  };
  inviter?: {
    name: string;
    email: string;
  } | null;
  invitee: {
    name?: string;
    email: string;
    accountExists?: boolean;
  } | null;
  role: "admin" | "member" | "viewer";
  invitedAt: string;
  expiresAt: string;
  message?: string;
}

interface SponsorshipAcceptResponse {
  billingOrgName?: string;
  acceptedAt?: string;
  homepageProvisioningApplied?: boolean;
  appliedProvisioningConfig?: {
    homeProjectId: string;
    homePageId: string | null;
  } | null;
  redirectUrl?: string;
}

type SponsorshipPreviewStatus =
  | "pending"
  | "already_accepted"
  | "already_declined"
  | "expired"
  | "canceled"
  | "invalidated"
  | "removed";

type PageState =
  | "loading"
  | "valid"
  | "expired"
  | "invalid"
  | "already_accepted"
  | "already_declined"
  | "processing"
  | "success"
  | "error"
  | "login_required";

const SPONSORSHIP_VERIFIED_EMAIL_MISMATCH_MESSAGE =
  "This invitation can only be accepted by the account with the invited verified email address.";
const SPONSORSHIP_INVITE_TOKEN_PATTERN = /^[a-f0-9]{64}$/i;

type SwitchAccountDestination = "/login" | "/register" | `/register?${string}`;

function isSponsorshipPreviewStatus(
  value: string | undefined,
): value is SponsorshipPreviewStatus {
  return (
    value === "pending" ||
    value === "already_accepted" ||
    value === "already_declined" ||
    value === "expired" ||
    value === "canceled" ||
    value === "invalidated" ||
    value === "removed"
  );
}

function getSponsorshipPreviewMessage(
  status: SponsorshipPreviewStatus,
): string | null {
  switch (status) {
    case "expired":
      return "This sponsorship invitation has expired.";
    case "canceled":
      return "This sponsorship invitation was canceled.";
    case "invalidated":
    case "removed":
      return "This sponsorship invitation is no longer available.";
    default:
      return null;
  }
}

function isExplicitAlreadyAccepted(error: unknown): boolean {
  const errorData = errorUtils.getApiErrorRecord(error);
  return (
    errorData?.status === "already_accepted" ||
    errorData?.code === "ALREADY_ACCEPTED"
  );
}

function isSponsorshipVerifiedEmailMismatch(error: unknown): boolean {
  const message = errorUtils.getApiErrorMessage(error, "").toLowerCase();

  return (
    errorUtils.hasErrorCode(error, "INVITE_EMAIL_MISMATCH") ||
    message.includes("different verified email") ||
    message.includes("different email address")
  );
}

function buildRegistrationPath(
  sponsorshipInviteToken: string | undefined,
): "/register" | `/register?${string}` {
  return sponsorshipInviteToken &&
    SPONSORSHIP_INVITE_TOKEN_PATTERN.test(sponsorshipInviteToken)
    ? `/register?${new URLSearchParams({ sponsorshipInviteToken }).toString()}`
    : "/register";
}

/**
 * AcceptInvitationPage
 *
 * Standalone page for accepting organization invitations via email link.
 * Route: /invitations/accept/:token
 *
 * States:
 * 1. Loading: Validating invitation...
 * 2. Valid: Show org details + Accept/Decline buttons
 * 3. Expired: Show expiry message + contact admin suggestion
 * 4. Invalid: Show not found message
 * 5. Already accepted: Show success message with link to organization
 * 6. Login required: Prompt user to login first (when unauthenticated)
 */
export const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteKind = searchParams.get("kind");
  const isSponsorshipInvite = inviteKind === "sponsorship" || inviteKind === "onboarding-bundle";

  const { user, isAuthenticated } = useAuth();
  const { logout } = useAuthActions();
  const acceptByToken = useInvitationStore((state) => state.acceptByToken);
  const declineByToken = useInvitationStore((state) => state.declineByToken);
  const fetchOrganizations = useOrganizationStore(
    (state) => state.fetchOrganizations
  );

  const [pageState, setPageState] = useState<PageState>("loading");
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [acceptResult, setAcceptResult] = useState<AcceptInvitationResult | null>(null);

  // Validate the invitation token
  const validateToken = useCallback(async () => {
    if (!token) {
      setPageState("invalid");
      setErrorMessage("No invitation token provided.");
      return;
    }

    try {
      const invitationData = isSponsorshipInvite
        ? await api
            .get<{
              masterName: string;
              billingOrgName: string;
              invitedEmailMasked: string;
              invitedAccountExists?: boolean;
              expiresAt: string;
              message?: string;
              status?: SponsorshipPreviewStatus;
            }>(`/sponsorships/token/${token}/preview`)
            .then((response) => {
              const sponsorshipDetails: InvitationDetails = {
                status: response.data?.status ?? "pending",
                membershipId: token,
                organization: {
                  _id: "",
                  name: response.data?.billingOrgName ?? "TerraMatrix",
                  slug: "account",
                },
                inviter: {
                  name: response.data?.masterName ?? "A TerraMatrix user",
                  email: "",
                },
                invitee: {
                  name: response.data?.invitedEmailMasked ?? "Invitee",
                  email: response.data?.invitedEmailMasked ?? "",
                  accountExists: response.data?.invitedAccountExists,
                },
                role: "member",
                invitedAt: new Date().toISOString(),
                expiresAt: response.data?.expiresAt ?? new Date().toISOString(),
                message: response.data?.message,
              };
              return sponsorshipDetails;
            })
        : await api
            .get<InvitationDetails>(`/invitations/${token}`)
            .then((response) => response.data);

      if (invitationData) {
        setInvitation(invitationData);

        if (invitationData.status === "already_accepted") {
          setPageState("already_accepted");
          return;
        }

        if (invitationData.status === "already_declined") {
          setPageState("already_declined");
          return;
        }

        if (isSponsorshipInvite) {
          const sponsorshipStatus = isSponsorshipPreviewStatus(
            invitationData.status,
          )
            ? invitationData.status
            : undefined;
          if (sponsorshipStatus === "expired") {
            setPageState("expired");
            setErrorMessage(getSponsorshipPreviewMessage(sponsorshipStatus) ?? "");
            return;
          }
          if (
            sponsorshipStatus === "canceled" ||
            sponsorshipStatus === "invalidated" ||
            sponsorshipStatus === "removed"
          ) {
            setPageState("invalid");
            setErrorMessage(getSponsorshipPreviewMessage(sponsorshipStatus) ?? "");
            return;
          }
        } else if (invitationData.status === "expired") {
          setPageState("expired");
          return;
        }

        // Check if user is authenticated
        if (!isAuthenticated) {
          if (
            isSponsorshipInvite &&
            invitationData.invitee?.accountExists === false
          ) {
            sessionStorage.setItem(
              "invitation_redirect",
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            );
            navigate(buildRegistrationPath(token), { replace: true });
            return;
          }
          setPageState("login_required");
        } else {
          setPageState("valid");
        }
      } else {
        setPageState("invalid");
        setErrorMessage("Invalid invitation link.");
      }
    } catch (error: unknown) {
      const status = errorUtils.getErrorStatus(error);
      const message = errorUtils.getApiErrorMessage(error, "");

      if (status === 410) {
        // Gone - expired
        setPageState("expired");
        setErrorMessage(message || "This invitation has expired.");
      } else if (status === 404) {
        // Not found - invalid or already used
        if (isExplicitAlreadyAccepted(error)) {
          setPageState("already_accepted");
          setErrorMessage(message);
        } else {
          setPageState("invalid");
          setErrorMessage(message || "This invitation link is invalid or has already been used.");
        }
      } else {
        setPageState("error");
        setErrorMessage(message || "Failed to validate invitation. Please try again.");
      }
    }
  }, [token, isAuthenticated, isSponsorshipInvite, navigate]);

  // Validate on mount and when authentication changes
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Handle accept invitation
  const handleAccept = async () => {
    if (!token) return;

    setPageState("processing");

    try {
      if (isSponsorshipInvite) {
        const response = await api.post<SponsorshipAcceptResponse>(
          `/sponsorships/token/${token}/accept`,
        );
        const result: AcceptInvitationResult = {
          organization: {
            _id: invitation?.organization._id ?? "",
            name:
              response.data?.billingOrgName ??
              invitation?.organization.name ??
              "TerraMatrix",
            slug: invitation?.organization.slug ?? "account",
          },
          role: invitation?.role ?? "sponsored",
          acceptedAt: response.data?.acceptedAt ?? new Date().toISOString(),
          appliedProvisioningConfig:
            response.data?.appliedProvisioningConfig ?? null,
          redirectUrl: response.data?.redirectUrl ?? "/account/people",
        };
        setAcceptResult(result);
        setPageState("success");
        toast.success("Sponsorship accepted", {
          description: `You're ready to continue in ${
            result.organization?.name ?? "your sponsored workspace"
          }.`,
        });
        const redirectPath = getInvitationAcceptRedirect(result);
        setTimeout(() => {
          navigate(redirectPath);
        }, 2000);
        return;
      }

      const result = await acceptByToken(token);
      setAcceptResult(result);
      setPageState("success");
      const redirectPath = getInvitationAcceptRedirect(result);
      const organizationName = result.organization?.name ?? invitation?.organization.name ?? "the organization";

      toast.success(`Welcome to ${organizationName}!`, {
        description: `You are now a ${invitation?.role || "member"} of the organization.`,
      });

      // Refresh organizations list
      await fetchOrganizations();

      // Auto-redirect after a short delay
      setTimeout(() => {
        navigate(redirectPath);
      }, 2000);
    } catch (error: unknown) {
      const fallbackMessage = "Failed to accept invitation. Please try again.";
      const errorMessage = isSponsorshipInvite &&
        isSponsorshipVerifiedEmailMismatch(error)
        ? SPONSORSHIP_VERIFIED_EMAIL_MISMATCH_MESSAGE
        : errorUtils.getApiErrorMessage(error, fallbackMessage);

      setPageState("error");
      setErrorMessage(errorMessage);

      toast.error("Failed to accept invitation", {
        description: errorMessage,
      });
    }
  };

  // Handle decline invitation
  const handleDecline = async () => {
    if (!token) return;

    setPageState("processing");

    try {
      if (isSponsorshipInvite) {
        await api.post(`/sponsorships/token/${token}/decline`);
      } else {
        await declineByToken(token);
      }

      toast.success("Invitation declined", {
        description: `You have declined the invitation to ${invitation?.organization.name}.`,
      });

      // Navigate to decline page or home
      const declineParams = new URLSearchParams({ success: "true" });
      if (isSponsorshipInvite && inviteKind) {
        declineParams.set("kind", inviteKind);
      }
      navigate(`/invitations/decline/${token}?${declineParams.toString()}`);
    } catch (error: unknown) {
      setPageState("valid"); // Go back to valid state
      setErrorMessage(
        errorUtils.getApiErrorMessage(
          error,
          "Failed to decline invitation. Please try again.",
        )
      );

      toast.error("Failed to decline invitation", {
        description: errorUtils.getApiErrorMessage(error, "Please try again."),
      });
    }
  };

  const preserveInvitationRedirect = useCallback(() => {
    sessionStorage.setItem(
      "invitation_redirect",
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }, []);

  // Handle login redirect
  const handleLogin = () => {
    preserveInvitationRedirect();
    navigate("/login");
  };

  const handleSwitchAccount = (destination: SwitchAccountDestination) => {
    preserveInvitationRedirect();
    if (!isAuthenticated) {
      navigate(destination);
      return;
    }

    setPageState("processing");
    logout()
      .then(() => {
        navigate(destination);
      })
      .catch(() => {
        setPageState("error");
        setErrorMessage("Unable to sign out. Please try again.");
      });
  };

  // Render role badge
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "default";
      case "member":
        return "secondary";
      case "viewer":
        return "outline";
      default:
        return "outline";
    }
  };

  // Format expiry date
  const formatExpiry = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    return expiry.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: BACKGROUND.secondary }}
    >
      {/* Header with logo */}
      <div className="mb-8">
        <Link to="/">
          <TerraMatrixBrand
            markClassName="h-10 w-10"
            nameClassName="text-3xl"
          />
        </Link>
      </div>

      {/* Card container */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          backgroundColor: BACKGROUND.primary,
          boxShadow: SHADOWS.xl,
        }}
      >
        {/* Loading State */}
        {pageState === "loading" && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Validating Invitation</h2>
            <p className="text-muted-foreground">
              Please wait while we verify your invitation...
            </p>
          </div>
        )}

        {/* Valid State */}
        {pageState === "valid" && invitation && (
          <>
            {/* Header */}
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">
                You're invited to join
              </h2>
              <h1 className="text-2xl font-bold mt-1">
                {invitation.organization.name}
              </h1>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Organization description */}
              {invitation.organization.description && (
                <p className="text-muted-foreground text-center mb-4">
                  {invitation.organization.description}
                </p>
              )}

              {/* Invitation details */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Invited by</span>
                  <span className="font-medium">{invitation.inviter?.name ?? "A TerraMatrix user"}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your role</span>
                  <Badge
                    variant={getRoleBadgeVariant(invitation.role)}
                    className="capitalize"
                  >
                    {invitation.role}
                  </Badge>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Expires</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatExpiry(invitation.expiresAt)}
                  </span>
                </div>
              </div>

              {/* Personal message */}
              {invitation.message && (
                <div
                  className="p-4 rounded-lg mb-6 text-sm italic"
                  style={{
                    backgroundColor: BACKGROUND.secondary,
                    color: TEXT.secondary,
                  }}
                >
                  "{invitation.message}"
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDecline}
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAccept}
                  style={{
                    backgroundColor: FOREST_GREEN.DEFAULT,
                    color: "white",
                  }}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Accept Invitation
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Login Required State */}
        {pageState === "login_required" && invitation && (
          <>
            {/* Header */}
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">
                You're invited to join
              </h2>
              <h1 className="text-2xl font-bold mt-1">
                {invitation.organization.name}
              </h1>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <User
                  className="h-12 w-12 mx-auto mb-3"
                  style={{ color: MID_GRAY.DEFAULT }}
                />
                <h3 className="text-lg font-medium mb-2">Sign in to continue</h3>
                <p className="text-muted-foreground text-sm">
                  Please sign in with your TerraMatrix account to accept this
                  invitation.
                </p>
              </div>

              {/* Invitation details preview */}
              <div className="space-y-3 mb-6 p-4 rounded-lg" style={{ backgroundColor: BACKGROUND.secondary }}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Invited by</span>
                  <span className="font-medium">{invitation.inviter?.name ?? "A TerraMatrix user"}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your role</span>
                  <Badge
                    variant={getRoleBadgeVariant(invitation.role)}
                    className="capitalize"
                  >
                    {invitation.role}
                  </Badge>
                </div>
              </div>

              {/* Login button */}
              <Button
                className="w-full"
                onClick={handleLogin}
                style={{
                  backgroundColor: FOREST_GREEN.DEFAULT,
                  color: "white",
                }}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In to Accept
              </Button>
            </div>
          </>
        )}

        {/* Processing State */}
        {pageState === "processing" && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Processing...</h2>
            <p className="text-muted-foreground">
              Please wait while we process your response...
            </p>
          </div>
        )}

        {/* Success State */}
        {pageState === "success" && acceptResult && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <Check className="h-8 w-8" style={{ color: FOREST_GREEN.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Welcome!</h2>
            <p className="text-muted-foreground mb-6">
              {isSponsorshipInvite
                ? `You have successfully accepted the sponsorship from ${acceptResult.organization?.name ?? invitation?.organization.name ?? "TerraMatrix"}.`
                : `You have successfully joined ${acceptResult.organization?.name ?? invitation?.organization.name ?? "the organization"} as a ${invitation?.role || "member"}.`}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {isSponsorshipInvite
                ? "Redirecting you to your homepage..."
                : "Redirecting you to the organization..."}
            </p>
            <Button
              onClick={() => navigate(getInvitationAcceptRedirect(acceptResult))}
              style={{
                backgroundColor: FOREST_GREEN.DEFAULT,
                color: "white",
              }}
            >
              {isSponsorshipInvite ? "Continue" : "Go to Organization"}
            </Button>
          </div>
        )}

        {/* Expired State */}
        {pageState === "expired" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: WARNING.bg }}
            >
              <Clock className="h-8 w-8" style={{ color: WARNING.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invitation Expired</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                "This invitation has expired. Please contact the organization administrator for a new invitation."}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Invalid State */}
        {pageState === "invalid" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                "This invitation link is invalid or has already been used."}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Already Accepted State */}
        {pageState === "already_accepted" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <Check className="h-8 w-8" style={{ color: FOREST_GREEN.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Accepted</h2>
            <p className="text-muted-foreground mb-6">
              You have already accepted this invitation.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              style={{
                backgroundColor: FOREST_GREEN.DEFAULT,
                color: "white",
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Already Declined State */}
        {pageState === "already_declined" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: WARNING.bg }}
            >
              <X className="h-8 w-8" style={{ color: WARNING.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Declined</h2>
            <p className="text-muted-foreground mb-6">
              You have already declined this invitation.
            </p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Error State */}
        {pageState === "error" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                "An error occurred while processing your invitation."}
            </p>
            {isSponsorshipInvite &&
              errorMessage === SPONSORSHIP_VERIFIED_EMAIL_MISMATCH_MESSAGE && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
                  <p className="font-medium">Use the invited email address</p>
                  <p className="mt-1">
                    {user?.email
                      ? `You are currently signed in as ${user.email}.`
                      : "You are not signed in with the invited account."}{" "}
                    Sign in with the account that owns the invited verified
                    email, or create that account if it does not exist yet.
                  </p>
                </div>
              )}
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button onClick={() => validateToken()}>Try Again</Button>
            </div>
            {isSponsorshipInvite &&
              errorMessage === SPONSORSHIP_VERIFIED_EMAIL_MISMATCH_MESSAGE && (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <Button
                    variant="outline"
                    onClick={() => handleSwitchAccount("/login")}
                  >
                    Sign in with invited email
                  </Button>
                  {invitation?.invitee?.accountExists === false && (
                    <Button
                      onClick={() =>
                        handleSwitchAccount(buildRegistrationPath(token))
                      }
                    >
                      Create invited account
                    </Button>
                  )}
                </div>
              )}
          </div>
        )}
      </div>

      {/* Footer text */}
      <p className="mt-8 text-sm text-muted-foreground text-center">
        If you weren't expecting this invitation, you can safely ignore it.
      </p>
    </div>
  );
};

export default AcceptInvitationPage;
